import { useTranslate } from '@refinedev/core';
import { Empty, theme } from 'antd';
import {
  GUTTER,
  MOBILE_PLACEHOLDER,
} from '@/components/mobile_preview/constants';
import { useMobilePreview } from '@/components/mobile_preview/mobilePreviewContext';
import { SwipeCarousel } from '@/components/mobile_preview/SwipeCarousel';
import {
  asDeviceWidth,
  CORNER_RADIUS,
  PAGE_GAP,
  PAGE_WIDTH,
  RATIO,
} from './bannerPreview';
import type { PreviewModel } from './bannerPreview';
import { CreativeSlot } from '@/components/user_catalog/creative/CreativeSlot';

interface BannerPreviewSectionProps {
  model: PreviewModel;
  /** 1-based page currently under the viewport. */
  selectedPage?: number;
  /** Supplied to make the strip draggable; omit for a static preview. */
  onSwipe?: (page: number) => void;
  /** Creatives still in flight — show a bare slot, not the empty state. */
  loading?: boolean;
}

/**
 * The banner slot as it sits in the feed — frame-agnostic, so it can
 * be stacked with other section previews inside one PhoneFrame.
 */
export const BannerPreviewSection = ({
  model,
  selectedPage = 1,
  onSwipe,
  loading = false,
}: BannerPreviewSectionProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { mobileTheme } = useMobilePreview();

  const size = model.effectiveSize;
  const count = model.entries.length;

  if (loading || !size || count === 0) {
    return (
      <div style={{ padding: `0 ${asDeviceWidth(GUTTER)}` }}>
        <div
          style={{
            aspectRatio: `${RATIO.small.w} / ${RATIO.small.h}`,
            borderRadius: CORNER_RADIUS,
            background: loading
              ? MOBILE_PLACEHOLDER[mobileTheme]
              : token.colorFillSecondary,
          }}
        >
          {!loading && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('user_catalog.banners.preview.empty')}
              style={{ margin: 0, paddingTop: 24 }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <SwipeCarousel
      // The carousel counts from 0; pages are 1-based everywhere else here.
      // Without a listener the host has no page to drive, so the track keeps
      // its own — the strip stays swipeable wherever it is embedded.
      value={onSwipe ? selectedPage - 1 : undefined}
      onChange={onSwipe ? (index) => onSwipe(index + 1) : undefined}
      // A page stops short of both gutters, so its neighbours peek.
      slideWidth={asDeviceWidth(PAGE_WIDTH)}
      gap={asDeviceWidth(PAGE_GAP)}
      inset={asDeviceWidth(GUTTER)}
      // The panel's Segmented is the pager for this surface.
      dots={false}
      slides={model.entries.map((entry) => (
        <CreativeSlot
          key={entry.link.id}
          imageUrl={entry.action.image_url}
          alt={entry.action.name}
          // Every page takes the first renderable page's ratio — the
          // carousel forces it, so a clashing creative crops.
          size={size}
        />
      ))}
    />
  );
};
